import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, IconButton, Dialog, CircularProgress } from "@material-ui/core";
import { faPlus } from "@fortawesome/pro-light-svg-icons";
import { faIndustry } from "@fortawesome/pro-regular-svg-icons";
import { faPen } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { extendWithLoadingAndFail } from "App/Modules/Services";
import type { IManufacturersRepository } from "App/Modules/Repositories";
import type { IResponseWithLoading, IManufacturer } from "App/Modules/Models";

import { SearchField } from "App/ReactComponents/Shared";
import { AddManufacturer } from "./AddManufacturer";
import { EditManufacturer } from "./EditManufacturer";

import "./_manufacturers.scss";

type ManufacturersProps = {
    irManufacturersRepository: IManufacturersRepository;
};

export const Manufacturers = (props: ManufacturersProps) => {
    const { t } = useTranslation();
    const [globalFilter, setGlobalFilter] = useState("");
    const [manufacturersLoading, setManufacturersLoading] = useState(true);
    const [showAddManufacturer, setShowAddManufacturer] = useState(false);
    const [showEditManufacturer, setShowEditManufacturer] = useState(false);

    const [manufacturers, setManufacturers] = useState<IResponseWithLoading<IManufacturer[]>>(null);
    const [selectedManufacturer, setSelectedManufacturer] = useState<IManufacturer>(null);

    useEffect(() => {
        const sub = extendWithLoadingAndFail(props.irManufacturersRepository.getManufacturers()).subscribe(
            (response: IResponseWithLoading<IManufacturer[]>) => {
                if (!response.isLoading && !response.hasError && response.data) {
                    setManufacturers(response);
                    setManufacturersLoading(false);
                }
            },
            (error) => {
                setManufacturers(error);
                setManufacturersLoading(false);
            },
        );

        return () => {
            sub.unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterFunc = ({ name, corporateIdNumber }: IManufacturer) => {
        if (!globalFilter) {
            return true;
        }
        return (
            name.toLowerCase().indexOf(globalFilter.toLowerCase()) > -1 ||
            corporateIdNumber?.toLowerCase().indexOf(globalFilter.toLowerCase()) > -1
        );
    };

    const onAddSuccess = (addedManufacturer: IManufacturer) => {
        setManufacturers((current) => ({ ...current, data: [...manufacturers.data, addedManufacturer] }));
    };

    const onEditSuccess = (updatedManufacturer: IManufacturer) => {
        setManufacturers((current) => ({
            ...current,
            data: [...current.data].map((item) => (item.id === updatedManufacturer.id ? updatedManufacturer : item)),
        }));
    };

    return (
        <div className="card manufacturers">
            <h1 className="title title--h2">{t("BeforeLogin.Manufacturer.Manufacturers")}</h1>
            <div className="manufacturers__functions">
                <div className="equipment-types__filter">
                    <SearchField globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
                </div>
                <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    startIcon={<FontAwesomeIcon icon={faPlus} fixedWidth />}
                    onClick={() => {
                        setShowAddManufacturer(true);
                    }}
                >
                    {`${t("Gui.Buttons.Add")} ${t("Entities.Manufacturer.Manufacturer")}`}
                </Button>
            </div>
            <div className="scrollable-list card card--nested card--alt">
                <div className="scrollable-list__scroller scrollable-list__scroller--large">
                    <div className="entity-list">
                        {manufacturersLoading ? (
                            <div className="entity-list__item entity-list__item--loading">
                                <CircularProgress />
                            </div>
                        ) : null}
                        {manufacturers?.data?.filter(filterFunc).map((manufacturer) => (
                            <div className="entity-list__item" key={manufacturer.id}>
                                <div className="entity-list__item-content entity-list__item-content--padding">
                                    <div className="entity-list__item-icon entity-list__item-icon--alt">
                                        <FontAwesomeIcon icon={faIndustry} />
                                    </div>
                                    <div className="entity-list__item-panel">
                                        <p className="paragraph paragraph--no-spacing">{manufacturer.name}</p>
                                        {manufacturer.corporateIdNumber ? (
                                            <p className="caption caption--small caption--no-spacing">
                                                {manufacturer.corporateIdNumber}
                                            </p>
                                        ) : null}
                                    </div>
                                    <div className="entity-list__item-icon entity-list__item-icon--alt">
                                        <IconButton
                                            className="icon-button"
                                            onClick={() => {
                                                setSelectedManufacturer(manufacturer);
                                                setShowEditManufacturer(true);
                                            }}
                                        >
                                            <FontAwesomeIcon className="icon-button__icon" icon={faPen} />
                                        </IconButton>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <Dialog open={showAddManufacturer} maxWidth="sm">
                <AddManufacturer
                    manufacturersRepository={props.irManufacturersRepository}
                    onAddSuccess={onAddSuccess}
                    onClose={() => {
                        setShowAddManufacturer(false);
                    }}
                />
            </Dialog>

            <Dialog open={showEditManufacturer} maxWidth="sm">
                <EditManufacturer
                    manufacturersRepository={props.irManufacturersRepository}
                    manufacturer={selectedManufacturer}
                    onEditSucess={onEditSuccess}
                    onClose={() => {
                        setShowEditManufacturer(false);
                        setSelectedManufacturer(null);
                    }}
                />
            </Dialog>
        </div>
    );
};
